/**
 * Client Entry Point
 * 
 * This file serves as the entry point for the client-side portion of the application.
 * It prioritizes fast hydration by loading only the active locale first, then
 * deferring non-critical setup (Sentry + remaining locale bundles) until after
 * the first paint path.
 * 
 * Key features:
 * - Sentry integration for client-side error monitoring and session replay
 * - Internationalization (i18n) setup with lazy locale loading
 * - React hydration with performance optimizations
 * - Multi-language support (English, Japanese, Korean)
 */

import * as Sentry from "@sentry/react-router";
import i18next from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { HydratedRouter } from "react-router/dom";
import { getInitialNamespaces } from "remix-i18next/client";

// Import i18n configuration and language resources
import i18n from "./i18n";
import ko from "./locales/ko";
import type { Translation } from "./locales/types";

type SupportedLocale = (typeof i18n.supportedLngs)[number];

const localeLoaders: Record<SupportedLocale, () => Promise<{ default: Translation }>> = {
  en: () => import("./locales/en"),
  ja: () => import("./locales/ja"),
  ko: () => import("./locales/ko"),
};

function getInitialLocale(): SupportedLocale {
  const htmlLang = document.documentElement.lang.trim().toLowerCase();
  const normalized = htmlLang.split("-")[0] as SupportedLocale;

  return i18n.supportedLngs.includes(normalized)
    ? normalized
    : (i18n.fallbackLng as SupportedLocale);
}

function initSentry() {
  if (import.meta.env.VITE_SENTRY_DSN && !import.meta.env.DEV) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
    });
  }
}

function initClientI18n(initialLocale: SupportedLocale, initialNamespaces: string[]) {
  i18next
    .use(
      resourcesToBackend(async (lng: string, ns: string) => {
        if (ns !== "common") {
          return {};
        }

        if (!i18n.supportedLngs.includes(lng as SupportedLocale)) {
          return {};
        }

        const module = await localeLoaders[lng as SupportedLocale]();
        return module.default;
      }),
    )
    .use(initReactI18next)
    .init({
      ...i18n,
      lng: initialLocale,
      ns: initialNamespaces,
      initImmediate: false,
      partialBundledLanguages: true,
      resources: {
        ko: {
          common: ko,
        },
      },
    });
}

/**
 * Hydration function for client-side initialization
 * 
 * This asynchronous function handles critical startup only:
 * 1. Detect active locale from server-rendered html lang
 * 2. Initialize i18next with that single locale
 * 3. Hydrate the React application immediately
 * 4. Defer non-critical setup to the next macrotask
 */
function hydrate() {
  const initialLocale = getInitialLocale();
  const initialNamespaces = getInitialNamespaces();
  initClientI18n(initialLocale, initialNamespaces);

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <HydratedRouter />
        </StrictMode>
      </I18nextProvider>,
    );
  });

  // Defer non-critical work so first interaction is not delayed.
  setTimeout(() => {
    initSentry();
    const localesToPreload = i18n.supportedLngs.filter((lng) => lng !== "ko");
    void i18next.loadLanguages(localesToPreload);
    void i18next.loadNamespaces(initialNamespaces);
  }, 0);
}

/**
 * Start hydration immediately so the app becomes interactive as early as possible.
 */
void hydrate();
